(async () => {
  const { Core } = await importModule('Core');

  const ATTR_COLS_D = 'cols-desktop';
  const ATTR_COLS_T = 'cols-tablet';
  const ATTR_COLS_M = 'cols-mobile';
  const ATTR_BLEED_L = 'bleed-left';
  const ATTR_BLEED_R = 'bleed-right';
  const ATTR_ARROWS = 'arrows';
  const ATTR_PAGINATION = 'pagination';
  const ATTR_SL_D = 'slider-desktop';
  const ATTR_SL_T = 'slider-tablet';
  const ATTR_SL_M = 'slider-mobile';
  const ATTR_LOOP = 'loop';

  const MOBILE_OFFSET_MULT = 3;

  customElements.define('custom-slider', class extends Core {
    elements = {
      swiper: '[data-swiper]',
      next: '[data-next]',
      prev: '[data-prev]',
      pagination: '[data-pagination]'
    }

    async render() {
      if (this._initStarted) return;
      this._initStarted = true;
      try {
        const { Swiper } = await importModule('Swiper');
        this._Swiper = Swiper;
        this._initSwiper();
      } finally {
        this.classList.add('is-ready');
      }
    }

    _initSwiper() {
      const Swiper = this._Swiper;
      const gap = this.gap;
      const gapMobile = this.gapMobile;

      const props = {
        slidesPerView: this.colsMobile,
        spaceBetween: gapMobile,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        loop: this.loopEnabled,
        enabled: this.sliderMobile,
        breakpoints: {
          0: { enabled: this.sliderMobile, slidesPerView: this.colsMobile, spaceBetween: gapMobile },
          768: { enabled: this.sliderTablet, slidesPerView: this.colsTablet, spaceBetween: gap },
          992: { enabled: this.sliderDesktop, slidesPerView: this.colsDesktop, spaceBetween: gap }
        }
      };

      if (this.hasArrows && this.$next && this.$prev) {
        props.navigation = {
          nextEl: this.$next,
          prevEl: this.$prev,
          disabledClass: 'swiper-button-disabled'
        };
      }

      if (this.hasPagination && this.$pagination) {
        props.pagination = { el: this.$pagination, type: 'bullets', clickable: true };
      }

      this.swiper = new Swiper(this.$swiper, props);
      this._shouldSlide = this._currentShouldSlide();

      if (!this._onResize) this._bindResize();

      this._refresh();
      requestAnimationFrame(() => this._refresh());
      setTimeout(() => this._refresh(), 300);
    }

    // Whether the current viewport is in slider mode (vs grid fallback).
    _currentShouldSlide() {
      const vw = document.documentElement.clientWidth;
      if (vw <= 767) return this.sliderMobile;
      if (vw <= 991) return this.sliderTablet;
      return this.sliderDesktop;
    }

    _refresh() {
      if (!this.swiper) return;
      if (this.hasBleedLeft || this.hasBleedRight) this._applyBleed();
      this.swiper.updateSize();
      this.swiper.updateSlides();
      this.swiper.update();
    }

    _bindResize() {
      this._onResize = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          // Rebuild across a grid <-> slider breakpoint change: Swiper can't
          // cleanly re-enable from a disabled (grid) state, so destroy and
          // re-init instead of a soft update.
          if (this._currentShouldSlide() !== this._shouldSlide) {
            this._rebuild();
          } else {
            this._refresh();
          }
        });
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    _rebuild() {
      if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
      this.swiper = null;
      this._initSwiper();
    }

    _applyBleed() {
      this.style.setProperty('--slider-bleed-left', '0px');
      this.style.setProperty('--slider-bleed-right', '0px');
      void this.offsetWidth;
      const viewport = document.documentElement.clientWidth;
      const rect = this.getBoundingClientRect();
      const pad = Math.max(0, Math.round(rect.left), Math.round(viewport - rect.right));
      this.style.setProperty('--slider-bleed-left', this.hasBleedLeft ? pad + 'px' : '0px');
      this.style.setProperty('--slider-bleed-right', this.hasBleedRight ? pad + 'px' : '0px');
      const offset = viewport <= 767 ? pad * MOBILE_OFFSET_MULT : pad;
      this.swiper.params.slidesOffsetBefore = offset;
      this.swiper.params.slidesOffsetAfter = offset;
    }

    disconnect() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
      this._initStarted = false;
    }

    get slideCount() { return this.$swiper.querySelectorAll('.swiper-slide').length; }
    get loopEnabled() {
      const perViews = [];
      if (this.sliderDesktop) perViews.push(this.colsDesktop);
      if (this.sliderTablet) perViews.push(this.colsTablet);
      if (this.sliderMobile) perViews.push(this.colsMobile);
      const maxPerView = perViews.length ? Math.max(...perViews) : 0;
      return this.hasLoop && this.slideCount > maxPerView;
    }
    get colsDesktop() { return parseFloat(this.getAttribute(ATTR_COLS_D)) || 4; }
    get colsTablet() { return parseFloat(this.getAttribute(ATTR_COLS_T)) || 2; }
    get colsMobile() { return parseFloat(this.getAttribute(ATTR_COLS_M)) || 2; }
    get gap() {
      const v = parseFloat(getComputedStyle(this).getPropertyValue('--g-section-gap'));
      return isNaN(v) ? 20 : v;
    }
    get gapMobile() {
      const v = parseFloat(getComputedStyle(this).getPropertyValue('--g-section-gap-mobile'));
      return isNaN(v) ? 16 : v;
    }
    get hasArrows() { return this.hasAttribute(ATTR_ARROWS); }
    get hasPagination() { return this.hasAttribute(ATTR_PAGINATION); }
    get hasBleedLeft() { return this.hasAttribute(ATTR_BLEED_L); }
    get hasBleedRight() { return this.hasAttribute(ATTR_BLEED_R); }
    get hasLoop() { return this.hasAttribute(ATTR_LOOP); }
    get sliderDesktop() { return this.hasAttribute(ATTR_SL_D); }
    get sliderTablet() { return this.hasAttribute(ATTR_SL_T); }
    get sliderMobile() { return this.hasAttribute(ATTR_SL_M); }
  });

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('custom-slider').forEach((el) => {
      if (el.swiper) el._refresh();
      else if (typeof el.render === 'function') el.render();
    });
  });
})();